import * as React from 'react'
import {Box, Typography, Chip, Button} from '@mui/material'
import {sectionCardSx, primaryBtnSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, ERROR} from '../../../../styles/foundation/colors'
import {GAP, PAD} from '../../../../styles/foundation/spacing'
import {FREEZE_LEVEL_LABEL, STATUS_TONE} from '../data/reference'
import {date, int} from '../format'
import {reasonGuide} from './logic'
import {chipSentenceSx} from '../components/tone'
import KeyValue from '../components/KeyValue'
import RqLevelChip from './RqLevelChip'
import RqSection from './RqSection'
import RqWeights from './RqWeights'
import RqTimeline from './RqTimeline'

/**
 * Weight Freeze (v2) — Requests: one rejected request.
 *
 * The reason band is this card's centre of gravity — a tinted box with a
 * left border carrying `req.reason` (the enum value itself, e.g.
 * 'Contradictory data available') plus `reasonGuide(req.reason)`, the
 * keyed-on-the-enum guidance text from logic.js. `round={1}` on the
 * timeline below does not truncate anything — `groupRounds` still emits
 * every round the history actually reaches (WF-NB-2609 reaches round 3
 * with an empty round 2 in between), it just stops this card from forcing
 * any EXTRA open round beyond what the record already shows.
 */
export default function RqRejectCard({req, pack, onFresh}) {
  const evidenceAwbCount = (req.evidenceAwbs || []).length

  const rightItems = [
    {label: 'Level', value: FREEZE_LEVEL_LABEL[req.level] || req.level},
    {label: 'Packing material', value: pack ? pack.label : 'Not set'},
    {label: 'DWS images', value: int(req.evidence || 0)},
    {label: 'Raised on', value: date(req.raisedOn)},
    {label: 'Attached records', value: int(evidenceAwbCount)},
  ]
  if (req.remarks) rightItems.push({label: 'Remarks', value: req.remarks, full: true})

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: GAP.sm, flexWrap: 'wrap', pb: GAP.sm, borderBottom: `1px solid ${BORDER}`,
      }}>
        <Box sx={{minWidth: 0, flex: '1 1 320px'}}>
          <Typography component='h3' sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY, m: 0}}>
            {req.subject}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: GAP.xs, flexWrap: 'wrap', mt: 0.35}}>
            <Typography sx={{fontFamily: 'monospace', fontSize: SIZE.xs, color: SUBTLE}}>{req.id}</Typography>
            <RqLevelChip level={req.level} />
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>
            Raised by {req.raisedBy} on {date(req.raisedOn)}
          </Typography>
        </Box>
        <Chip size='small' label='Rejected' sx={chipSentenceSx(STATUS_TONE.rejected)} />
      </Box>

      <Box
        // The house pattern for a tinted callout — a subtle all-round border rather than a
        // thick one-sided rule. `RqApproved` and `RqApprovedDrawer` use it too, so the three
        // request tabs read as one surface. Only `display`/`gap`/`p` are overridden, because
        // alertStripSx is a single-row flex strip and this callout is a two-line block.
        sx={{
          ...alertStripSx(ERROR),
          display: 'grid', gap: GAP.xs, p: PAD.card,
          alignItems: 'stretch',
          mt: GAP.md,
        }}
      >
        <Typography sx={{
          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: ERROR,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Reason for rejection
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY}}>
          {req.reason}
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6}}>
          {reasonGuide(req.reason)}
        </Typography>
      </Box>

      <Box sx={{
        display: 'grid', gap: GAP.lg, mt: GAP.md,
        gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)'},
      }}>
        <Box sx={{display: 'grid', gap: GAP.md, alignContent: 'start', minWidth: 0}}>
          <RqSection title='Shipment weight'>
            <RqWeights req={req} compact />
          </RqSection>
          <RqSection title='What was asked for'>
            <KeyValue items={rightItems} columns={2} />
          </RqSection>
        </Box>
        <RqTimeline history={req.history} round={1} label='Decline history' />
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: GAP.md, flexWrap: 'wrap',
        mt: GAP.md, pt: GAP.md, borderTop: `1px solid ${BORDER}`,
      }}>
        <Button sx={primaryBtnSx} onClick={() => onFresh(req)}>Raise a fresh request</Button>
        <Typography sx={{
          fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5,
          flex: '1 1 240px', minWidth: 0,
        }}>
          A fresh request starts blank. {req.id} cannot be reopened, edited or used as a starting point.
        </Typography>
      </Box>
    </Box>
  )
}
